import React, { useState, useEffect, useRef, useCallback, useImperativeHandle } from 'react';
import {
  Button, Card, Input, Space, Typography, Popconfirm, message, Tooltip, Tag, Empty,
  Spin, Alert,
} from 'antd';
import {
  AudioOutlined, StopOutlined, SaveOutlined, EditOutlined, DeleteOutlined,
  PlusOutlined, SoundOutlined, LoadingOutlined, AudioFilled,
  CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, DownOutlined, UpOutlined,
} from '@ant-design/icons';
import axios from '../offline/profileApi';
import { queueReportFiles, isProfileQueued } from '../offline/profileActions';
import { cacheOwner } from '../offline/profileCache';
import { OfflineAudio } from '../offline/OfflineMedia';
import moment from 'moment';
import { ref, getDownloadURL } from 'firebase/storage';
import { uploadBytes } from '../utils/protectedStorage';
import { storage } from '../firebaseConfig';
import { Patient, VoiceNote } from './type';
import { useLanguage } from './LanguageContext';
import API from '../config/api';

const { Text } = Typography;

const CHUNK_MS = 250;
const MAX_RECORDING_SECONDS = 8 * 60;

// ─── Step status ──────────────────────────────────────────────────────────────
type StepStatus = 'idle' | 'loading' | 'done' | 'error';

// ─── Step progress bar ────────────────────────────────────────────────────────
interface Step { key: string; labelAr: string; labelEn: string; status: StepStatus; error?: string; }

function StepProgress({ steps, isRTL }: { steps: Step[]; isRTL: boolean }) {
  const color = (s: StepStatus) => {
    if (s === 'done')    return '#52c41a';
    if (s === 'error')   return '#ff4d4f';
    if (s === 'loading') return '#722ed1';
    return '#d9d9d9';
  };
  const icon = (s: StepStatus) => {
    if (s === 'loading') return <Spin size="small" />;
    if (s === 'done')    return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 15 }} />;
    if (s === 'error')   return <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 15 }} />;
    return <span style={{ width: 14, height: 14, display: 'inline-block', borderRadius: '50%', border: '2px solid #d9d9d9', verticalAlign: 'middle' }} />;
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', margin: '10px 0 4px' }}>
      {steps.map((step, i) => (
        <React.Fragment key={step.key}>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
            padding: '6px 12px', borderRadius: 8, minWidth: 90, textAlign: 'center',
            background: step.status === 'idle' ? '#f5f5f5' : `${color(step.status)}12`,
            border: `1.5px solid ${color(step.status)}`,
          }}>
            {icon(step.status)}
            <Text style={{ fontSize: 11, fontWeight: 600, color: color(step.status), marginTop: 2 }}>
              {isRTL ? step.labelAr : step.labelEn}
            </Text>
            {step.error && (
              <Text style={{ fontSize: 10, color: '#ff4d4f', maxWidth: 85, lineHeight: 1.2 }}>
                {step.error}
              </Text>
            )}
          </div>
          {i < steps.length - 1 && (
            <div style={{ width: 20, height: 2, background: color(step.status), margin: '0 2px' }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Legacy marker stripping (old saved notes only) ───────────────────────────
function stripLegacyMarkers(s: string) {
  return s.replace(/\[\[REVIEW\]\]|\[\[\/REVIEW\]\]|\[\[PRED\]\]|\[\[\/PRED\]\]/g, '');
}
function hasLegacyMarkers(s: string) {
  return s.includes('[[REVIEW]]') || s.includes('[[PRED]]');
}

// ─── Web Speech API type shims ────────────────────────────────────────────────
interface ISpeechRecognitionEvent {
  resultIndex: number;
  results: { length: number; [i: number]: { isFinal: boolean; [i: number]: { transcript: string } } };
}
interface ISpeechRecognition extends EventTarget {
  lang: string; continuous: boolean; interimResults: boolean; maxAlternatives: number;
  start(): void; stop(): void; abort(): void;
  onresult: ((ev: ISpeechRecognitionEvent) => void) | null;
  onerror:  ((ev: Event) => void) | null;
  onend:    (() => void) | null;
}
declare global {
  interface Window {
    SpeechRecognition?: new () => ISpeechRecognition;
    webkitSpeechRecognition?: new () => ISpeechRecognition;
  }
}

interface VoiceNoteSectionProps {
  patient: Patient | null;
  onPatientUpdate?: (updated: Patient) => void;
  doctorSpecialization?: string;
}

export interface VoiceNoteSectionHandle {
  openRecorder: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────

const VoiceNoteSection = React.forwardRef<VoiceNoteSectionHandle, VoiceNoteSectionProps>(({
  patient, onPatientUpdate, doctorSpecialization = '',
}, forwardedRef) => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const sectionRef = useRef<HTMLDivElement | null>(null);

  // ── Saved notes ──────────────────────────────────────────────────────────
  const [notes, setNotes] = useState<VoiceNote[]>([]);
  const [expandedNotes, setExpandedNotes] = useState<Set<number>>(new Set());

  // ── Recorder UI ──────────────────────────────────────────────────────────
  const [showRecorder,     setShowRecorder]     = useState(false);
  const [isRecording,      setIsRecording]      = useState(false);
  const [isTranscribing,   setIsTranscribing]   = useState(false);
  const [saving,           setSaving]           = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [micSupported,     setMicSupported]     = useState(true);

  // ── Per-step statuses ─────────────────────────────────────────────────────
  const [deepgramStatus, setDeepgramStatus] = useState<StepStatus>('idle');
  const [whisperStatus,  setWhisperStatus]  = useState<StepStatus>('idle');
  const [gptStatus,      setGptStatus]      = useState<StepStatus>('idle');
  const [deepgramError,  setDeepgramError]  = useState('');
  const [whisperError,   setWhisperError]   = useState('');
  const [gptError,       setGptError]       = useState('');

  // ── Transcript results ────────────────────────────────────────────────────
  const [liveTranscript, setLiveTranscript] = useState('');
  const liveRef = useRef('');
  const [deepgramResult, setDeepgramResult] = useState('');
  const [offlineResult,  setOfflineResult]  = useState('');
  const [gptResult,      setGptResult]      = useState('');

  // editable override — only shown when doctor clicks "Edit" or GPT fails
  const [editingFinal,   setEditingFinal]   = useState(false);
  const [finalTranscript,setFinalTranscript]= useState('');

  // ── Audio ─────────────────────────────────────────────────────────────────
  const [localAudioUrl, setLocalAudioUrl] = useState('');
  const audioChunksRef     = useRef<Blob[]>([]);
  const audioBlobRef       = useRef<Blob | null>(null);
  const mimeTypeRef        = useRef('');
  const uploadedAudioUrl   = useRef('');   // set once Firebase upload succeeds

  // ── Web Speech (supplemental) ─────────────────────────────────────────────
  const webSpeechRef       = useRef<ISpeechRecognition | null>(null);
  const webSpeechActiveRef = useRef(false);
  const webSpeechText      = useRef('');
  const [webSpeechSupported] = useState(() => !!(window.SpeechRecognition || window.webkitSpeechRecognition));

  // ── Infra refs ────────────────────────────────────────────────────────────
  const stoppedByUserRef = useRef(false);
  const streamRef        = useRef<MediaStream | null>(null);
  const recorderRef      = useRef<MediaRecorder | null>(null);
  const wsRef            = useRef<WebSocket | null>(null);
  const timerRef         = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoGptRanRef    = useRef(false);
  // Incremented on every resetSession() — all in-flight async ops check this
  // before touching state, so a stale callback from a cancelled session can
  // never corrupt (or silently wipe) a new session's data.
  const sessionIdRef     = useRef(0);

  // ── Edit ──────────────────────────────────────────────────────────────────
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText,     setEditText]     = useState('');
  const [editSaving,   setEditSaving]   = useState(false);

  // ── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => { if (!navigator.mediaDevices?.getUserMedia) setMicSupported(false); }, []);
  useEffect(() => { setNotes(patient?.voice_notes ? [...patient.voice_notes].reverse() : []); }, [patient]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { return () => { stopEverything(); if (localAudioUrl) URL.revokeObjectURL(localAudioUrl); }; }, []);

  // Auto-GPT: both sources ready
  useEffect(() => {
    if (deepgramResult && offlineResult && !autoGptRanRef.current && gptStatus === 'idle') {
      autoGptRanRef.current = true;
      void runGpt(deepgramResult, offlineResult);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepgramResult, offlineResult]);

  // Auto-GPT fallback: Deepgram failed but Whisper done
  useEffect(() => {
    if (deepgramStatus === 'error' && offlineResult && !autoGptRanRef.current && gptStatus === 'idle') {
      autoGptRanRef.current = true;
      void runGpt('', offlineResult);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepgramStatus, offlineResult]);

  // Auto-GPT fallback: Whisper failed but Deepgram done
  useEffect(() => {
    if (whisperStatus === 'error' && deepgramResult && !autoGptRanRef.current && gptStatus === 'idle') {
      autoGptRanRef.current = true;
      void runGpt(deepgramResult, '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whisperStatus, deepgramResult]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((res, rej) => {
      const r = new FileReader();
      r.onloadend = () => res(r.result as string);
      r.onerror   = rej;
      r.readAsDataURL(blob);
    });

  // ── Firebase Storage upload ───────────────────────────────────────────────
  const uploadAudioToFirebase = useCallback(async (blob: Blob): Promise<string> => {
    const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
    const path = `voice-notes/${patient?._id ?? 'unknown'}/${Date.now()}.${ext}`;
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, blob);
    return await getDownloadURL(snapshot.ref);
  }, [patient?._id]);

  // ── Whisper ───────────────────────────────────────────────────────────────
  const runWhisper = useCallback(async (blob: Blob) => {
    const sid = sessionIdRef.current;   // capture before any await
    setWhisperStatus('loading');
    setWhisperError('');
    setOfflineResult('');
    try {
      const base64 = await blobToBase64(blob);
      if (sessionIdRef.current !== sid) return;   // session cancelled while encoding
      const res = await axios.post(`${API.BASE_URL}${API.ENDPOINTS.WHISPER}`, {
        audio: base64,
        mimeType: blob.type,
      });
      if (sessionIdRef.current !== sid) return;   // session cancelled while waiting for API
      if (res.data?.success) {
        const t = res.data.transcript || '';
        setOfflineResult(t);
        setWhisperStatus(t ? 'done' : 'error');
        if (!t) setWhisperError(isRTL ? 'لا يوجد نص' : 'Empty transcript');
      } else {
        throw new Error('No transcript in response');
      }
    } catch (err: unknown) {
      if (sessionIdRef.current !== sid) return;   // stale error — ignore
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setWhisperError(msg);
      setWhisperStatus('error');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRTL]);

  // ── GPT ───────────────────────────────────────────────────────────────────
  const runGpt = useCallback(async (dg: string, offline: string) => {
    const sid = sessionIdRef.current;   // capture before any await
    const dgTrim      = dg.trim();
    const offlineTrim = offline.trim();
    const wsTrim      = webSpeechText.current.trim();
    if (!dgTrim && !offlineTrim && !wsTrim) return;

    const parts: string[] = [];
    if (dgTrim)      parts.push(`[Deepgram Live]: ${dgTrim}`);
    if (offlineTrim) parts.push(`[Whisper Offline]: ${offlineTrim}`);
    if (wsTrim)      parts.push(`[Browser]: ${wsTrim}`);

    setGptStatus('loading');
    setGptError('');
    setGptResult('');
    try {
      const res = await axios.post(`${API.BASE_URL}${API.ENDPOINTS.GPT_CLEAN}`, {
        text: parts.join('\n'),
        specialization: (doctorSpecialization || '').trim(),
      });
      if (sessionIdRef.current !== sid) return;   // session cancelled while waiting for GPT
      if (res.data?.success && res.data.cleaned) {
        setGptResult(res.data.cleaned);
        setGptStatus('done');
        message.success(isRTL ? '🩺 الملف الطبي الهيكلي جاهز' : '🩺 Structured medical history ready');
      } else {
        throw new Error('Empty GPT response');
      }
    } catch (err: unknown) {
      if (sessionIdRef.current !== sid) return;   // stale error — ignore
      const msg = err instanceof Error ? err.message : 'GPT error';
      setGptError(msg);
      setGptStatus('error');
      message.error(isRTL ? 'فشل GPT — يمكنك إعادة المحاولة' : 'GPT failed — you can retry');
    }
  }, [isRTL, doctorSpecialization]);

  // ── Web Speech helpers ────────────────────────────────────────────────────
  const startWebSpeech = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    try {
      const sr = new SR();
      sr.lang = 'ar-EG'; sr.continuous = true; sr.interimResults = false; sr.maxAlternatives = 1;
      sr.onresult = (ev: ISpeechRecognitionEvent) => {
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          if (ev.results[i].isFinal) {
            const chunk = ev.results[i][0].transcript.trim();
            if (chunk) webSpeechText.current += (webSpeechText.current ? ' ' : '') + chunk;
          }
        }
      };
      sr.onerror = () => { /* supplemental — silent */ };
      sr.onend   = () => { if (webSpeechActiveRef.current && webSpeechRef.current === sr) try { sr.start(); } catch { /**/ } };
      sr.start();
      webSpeechRef.current = sr;
      webSpeechActiveRef.current = true;
    } catch { /* not critical */ }
  };

  const stopWebSpeech = () => {
    webSpeechActiveRef.current = false;
    if (webSpeechRef.current) { try { webSpeechRef.current.abort(); } catch { /**/ } webSpeechRef.current = null; }
  };

  // ── Hard stop all recording ───────────────────────────────────────────────
  const stopEverything = () => {
    stoppedByUserRef.current = false;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    stopWebSpeech();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') try { recorderRef.current.stop(); } catch { /**/ }
    recorderRef.current = null;
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (wsRef.current) { try { wsRef.current.close(); } catch { /**/ } wsRef.current = null; }
  };

  // ── Reset all processing state ────────────────────────────────────────────
  const resetSession = () => {
    sessionIdRef.current += 1;         // invalidate every in-flight async op
    audioChunksRef.current = [];
    audioBlobRef.current   = null;
    webSpeechText.current  = '';
    autoGptRanRef.current  = false;
    uploadedAudioUrl.current = '';
    if (localAudioUrl) { URL.revokeObjectURL(localAudioUrl); setLocalAudioUrl(''); }
    liveRef.current = '';
    setLiveTranscript(''); setDeepgramResult(''); setOfflineResult(''); setGptResult('');
    setDeepgramStatus('idle'); setWhisperStatus('idle'); setGptStatus('idle');
    setDeepgramError(''); setWhisperError(''); setGptError('');
    setFinalTranscript(''); setEditingFinal(false);
    setRecordingSeconds(0); setIsRecording(false); setIsTranscribing(false);
    setAudioUploadState('idle');
  };

  // ── Start recording ───────────────────────────────────────────────────────
  const startRecording = async () => {
    if (!micSupported) { message.error(isRTL ? 'الميكروفون غير مدعوم' : 'Microphone not supported'); return; }
    resetSession();
    const sid = sessionIdRef.current;   // capture AFTER reset so it matches this session

    try {
      const keyRes = await axios.get<{ success: boolean; key: string }>(`${API.BASE_URL}${API.ENDPOINTS.TRANSCRIBE_KEY}`);
      if (sessionIdRef.current !== sid) return;   // cancelled while fetching key
      if (!keyRes.data?.key) throw new Error('Transcription not configured on server');
      const dgKey = keyRes.data.key;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      if (sessionIdRef.current !== sid) {
        // Cancelled while waiting for mic — release immediately
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      streamRef.current   = stream;
      mimeTypeRef.current = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg']
        .find(m => MediaRecorder.isTypeSupported(m)) ?? '';

      setDeepgramStatus('loading');

      const ws = new WebSocket(
        'wss://api.deepgram.com/v1/listen?model=nova-3&language=ar-EG&smart_format=true',
        ['token', dgKey],
      );
      wsRef.current = ws;

      ws.onopen = () => {
        const mimeType = mimeTypeRef.current;
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
        recorderRef.current = recorder;
        recorder.ondataavailable = (e: BlobEvent) => {
          if (e.data.size > 0) {
            audioChunksRef.current.push(e.data);
            if (ws.readyState === WebSocket.OPEN) ws.send(e.data);
          }
        };
        recorder.start(CHUNK_MS);
      };

      ws.onmessage = (event: MessageEvent) => {
        if (sessionIdRef.current !== sid) return;   // stale session — discard
        try {
          const msg = JSON.parse(event.data as string);
          if (msg.type === 'Results' && msg.is_final) {
            const text: string = msg.channel?.alternatives?.[0]?.transcript ?? '';
            if (text) {
              liveRef.current = liveRef.current ? `${liveRef.current} ${text}` : text;
              setLiveTranscript(liveRef.current);
            }
          } else if (msg.type === 'Metadata') {
            const result = liveRef.current.trim();
            setIsTranscribing(false);
            setDeepgramResult(result);
            setDeepgramStatus(result ? 'done' : 'error');
            if (!result) setDeepgramError(isRTL ? 'لا يوجد نص' : 'No speech detected');
            stoppedByUserRef.current = false;
            wsRef.current = null;
          }
        } catch { /* ignore */ }
      };

      ws.onerror = () => {
        if (sessionIdRef.current !== sid) return;   // stale — ignore
        setDeepgramStatus('error');
        setDeepgramError(isRTL ? 'خطأ في الاتصال' : 'Connection error');
        setIsTranscribing(false);
        setIsRecording(false);
      };
      ws.onclose = () => console.log('[VoiceNotes][DG] WS closed');

      startWebSpeech();
      setIsRecording(true);
      setRecordingSeconds(0);
      timerRef.current = setInterval(() => setRecordingSeconds(s => {
        const n = s + 1;
        if (n >= MAX_RECORDING_SECONDS) {
          message.warning(isRTL ? 'انتهى وقت التسجيل (8 دقائق)' : 'Max recording time (8 min)');
          stopRecording();
        }
        return n;
      }), 1000);

    } catch (err: unknown) {
      if (sessionIdRef.current !== sid) return;   // stale error after cancel
      const name = err && typeof err === 'object' && 'name' in err ? (err as { name: string }).name : '';
      if (name === 'NotAllowedError') message.error(isRTL ? 'يرجى السماح بالوصول إلى الميكروفون' : 'Please allow microphone access');
      else message.error(isRTL ? 'تعذر بدء التسجيل' : 'Could not start recording');
      setDeepgramStatus('error');
    }
  };

  // ── Stop recording ────────────────────────────────────────────────────────
  const stopRecording = () => {
    const sid = sessionIdRef.current;   // capture NOW — onstop fires asynchronously
    stoppedByUserRef.current = true;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    stopWebSpeech();

    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.onstop = () => {
        if (sessionIdRef.current !== sid) return;   // session was cancelled before onstop fired
        const mimeType = mimeTypeRef.current || 'audio/webm';
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        audioBlobRef.current = blob;
        setLocalAudioUrl(URL.createObjectURL(blob));
        if (!navigator.onLine && cacheOwner()) {
          setWhisperStatus('error');
          setWhisperError('Transcription requires internet. You can save the recording on this device.');
          setAudioUploadState('failed');
          return;
        }
        // Start Whisper transcription and Firebase upload in parallel
        void runWhisper(blob);
        setAudioUploadState('uploading');
        uploadAudioToFirebase(blob)
          .then(url => {
            if (sessionIdRef.current === sid) {
              uploadedAudioUrl.current = url;
              setAudioUploadState('done');
            }
          })
          .catch(err => {
            if (sessionIdRef.current === sid) {
              console.warn('[VoiceNotes] Firebase upload failed:', err);
              setAudioUploadState('failed');
            }
          });
      };
      recorderRef.current.stop();
    }
    recorderRef.current = null;

    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }

    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      setIsTranscribing(true);
      setTimeout(() => {
        if (sessionIdRef.current !== sid) return;   // cancelled during the 300 ms
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'CloseStream' }));
      }, 300);
    } else {
      if (sessionIdRef.current === sid) {
        const result = liveRef.current.trim();
        setDeepgramResult(result);
        setDeepgramStatus(result ? 'done' : 'error');
        if (!result) setDeepgramError(isRTL ? 'لا يوجد نص' : 'No speech detected');
      }
    }

    setIsRecording(false);
    setRecordingSeconds(0);
  };

  // ── Open / Cancel recorder ────────────────────────────────────────────────
  const handleOpenRecorder = () => {
    stopEverything();
    resetSession();
    setShowRecorder(true);
    window.requestAnimationFrame(() => window.setTimeout(() => { void startRecording(); }, 150));
  };

  const handleCancelRecorder = () => {
    stopEverything();
    resetSession();
    setShowRecorder(false);
  };

  useImperativeHandle(forwardedRef, () => ({
    openRecorder: () => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      handleOpenRecorder();
    },
  }));

  // ── New recording (within same session) ──────────────────────────────────
  const handleNewRecording = () => {
    stopEverything();
    resetSession();
    window.setTimeout(() => { void startRecording(); }, 150);
  };

  // ── Audio upload state (shown to user when upload is pending / failed) ───────
  const [audioUploadState, setAudioUploadState] = useState<'idle' | 'uploading' | 'done' | 'failed'>('idle');

  // Retry Firebase upload (called from UI when upload previously failed)
  const retryAudioUpload = useCallback(async () => {
    if (!audioBlobRef.current) return;
    setAudioUploadState('uploading');
    try {
      const url = await uploadAudioToFirebase(audioBlobRef.current);
      uploadedAudioUrl.current = url;
      setAudioUploadState('done');
      message.success(isRTL ? 'تم رفع الصوت بنجاح' : 'Audio uploaded successfully');
    } catch {
      setAudioUploadState('failed');
      message.error(isRTL ? 'فشل رفع الصوت — حاول مرة أخرى' : 'Audio upload failed — please retry');
    }
  }, [uploadAudioToFirebase, isRTL]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSaveNote = async () => {
    const text = (editingFinal ? finalTranscript : gptResult).trim();
    if (!text && !(audioBlobRef.current && !navigator.onLine && cacheOwner())) { message.warning(isRTL ? 'لا يوجد نص للحفظ' : 'Nothing to save'); return; }
    if (!patient?._id) return;
    setSaving(true);
    try {
      const doctorId = patient.doctor_id || localStorage.getItem('doctorId');

      if (!navigator.onLine && cacheOwner() && audioBlobRef.current) {
        const blob = audioBlobRef.current;
        await queueReportFiles(patient, [new File([blob], 'voice-note.webm', { type: blob.type })], {
          voice_notes: [...(patient.voice_notes || []), { text: text || 'Audio note (not transcribed)', createdAt: new Date().toISOString() }],
        }, 'voice');
        message.warning('Audio and note saved on this device — pending sync.');
        handleCancelRecorder();
        return;
      }

      // Use already-uploaded Firebase URL if available, else try uploading now.
      // Audio is stored in Firebase Storage — we never embed base64 in the request
      // body (can be several MB, causes H12 timeouts and MongoDB 16 MB limit).
      // If upload fails, save the text note anyway so the transcription is not lost.
      let audioUrl = uploadedAudioUrl.current;
      if (!audioUrl && audioBlobRef.current) {
        try {
          setAudioUploadState('uploading');
          audioUrl = await uploadAudioToFirebase(audioBlobRef.current);
          uploadedAudioUrl.current = audioUrl;
          setAudioUploadState('done');
        } catch (upErr) {
          console.warn('[VoiceNotes] Firebase upload failed — saving text only:', upErr);
          setAudioUploadState('failed');
          message.warning(isRTL
            ? 'تعذّر رفع الصوت — سيُحفظ النص فقط'
            : 'Audio upload failed — saving text only');
        }
      }

      const newNote: VoiceNote = {
        text,
        createdAt: new Date().toISOString(),
        ...(audioUrl ? { audioUrl } : {}),
      };
      const updatedNotes = [...(patient.voice_notes ?? []), newNote];
      const saveResp = await axios.put(
        `${API.BASE_URL}${API.ENDPOINTS.PATIENT_BY_ID(patient._id)}`,
        { voice_notes: updatedNotes },
        { params: { doctor_id: doctorId } },
      );

      message.success(t('voiceNoteSaved'));
      // Backend returns voice_notes with MongoDB-assigned _ids; prefer that over
      // the local array so subsequent edits/deletes reference stable _ids.
      const mergedNotes: VoiceNote[] = saveResp?.data?.patient?.voice_notes ?? updatedNotes;
      onPatientUpdate?.({ ...patient, voice_notes: mergedNotes });
      setNotes([...mergedNotes].reverse());
      handleCancelRecorder();
    } catch (error) {
      if (isProfileQueued(error)) { message.warning('Note saved on this device — pending sync.'); handleCancelRecorder(); }
      else message.error(t('voiceNoteSaveFailed'));
    } finally { setSaving(false); }
  };

  // ── Edit / Delete saved notes ─────────────────────────────────────────────
  const handleStartEdit = (note: VoiceNote, index: number) => { setEditingIndex(index); setEditText(note.text); };

  const handleSaveEdit = async (noteIndex: number) => {
    if (!patient?._id) return;
    const text = editText.trim(); if (!text) return;
    setEditSaving(true);
    try {
      const doctorId = patient.doctor_id || localStorage.getItem('doctorId');
      const existing = patient.voice_notes ? [...patient.voice_notes] : [];
      const original = existing.length - 1 - noteIndex;
      existing[original] = { ...existing[original], text, updatedAt: new Date().toISOString() };
      await axios.put(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_BY_ID(patient._id)}`, { voice_notes: existing }, { params: { doctor_id: doctorId } });
      message.success(t('voiceNoteSaved'));
      onPatientUpdate?.({ ...patient, voice_notes: existing });
      setNotes([...existing].reverse()); setEditingIndex(null);
    } catch (error) {
      if (isProfileQueued(error)) { message.warning('Note edit saved on this device — pending sync.'); setEditingIndex(null); }
      else message.error(t('voiceNoteSaveFailed'));
    } finally { setEditSaving(false); }
  };

  const handleDeleteNote = async (noteIndex: number) => {
    if (!patient?._id) return;
    try {
      const doctorId = patient.doctor_id || localStorage.getItem('doctorId');
      const existing = patient.voice_notes ? [...patient.voice_notes] : [];
      existing.splice(existing.length - 1 - noteIndex, 1);
      await axios.put(`${API.BASE_URL}${API.ENDPOINTS.PATIENT_BY_ID(patient._id)}`, { voice_notes: existing }, { params: { doctor_id: doctorId } });
      message.success(t('voiceNoteDeleted'));
      onPatientUpdate?.({ ...patient, voice_notes: existing });
      setNotes([...existing].reverse());
    } catch (error) {
      if (isProfileQueued(error)) message.warning('Note removal saved on this device — pending sync.');
      else message.error(t('voiceNoteDeleteFailed'));
    }
  };

  const formatTime = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const processingDone = !isRecording && !isTranscribing &&
    (deepgramStatus !== 'idle' || whisperStatus !== 'idle');

  const steps: Step[] = [
    { key: 'deepgram', labelAr: 'Deepgram', labelEn: 'Deepgram', status: deepgramStatus, error: deepgramError },
    { key: 'whisper',  labelAr: 'Whisper',  labelEn: 'Whisper',  status: whisperStatus,  error: whisperError  },
    { key: 'gpt',      labelAr: 'GPT AI',   labelEn: 'GPT AI',   status: gptStatus,      error: gptError      },
  ];

  const canSave = !isRecording && !isTranscribing && gptStatus !== 'loading' &&
    (!!(editingFinal ? finalTranscript.trim() : gptResult.trim()) || (!navigator.onLine && !!cacheOwner() && !!audioBlobRef.current));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div ref={sectionRef} id="patient-voice-notes" className="voice-note-section" style={{ marginTop: 8 }}>
      {/* Header */}
      <div className="voice-note-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <Space>
          <SoundOutlined style={{ color: '#722ed1', fontSize: 18 }} />
          <Text strong style={{ fontSize: 16 }}>{t('voiceNotesTitle')}</Text>
          {notes.length > 0 && <Tag color="purple">{notes.length}</Tag>}
        </Space>
        {!showRecorder && (
          <Button className="voice-note-add-button" type="primary" icon={<PlusOutlined />} onClick={handleOpenRecorder}
            style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}>
            {t('voiceNoteAddNew')}
          </Button>
        )}
      </div>

      {/* ── Recorder card ─────────────────────────────────────────────── */}
      {showRecorder && (
        <Card size="small" className="voice-note-recorder-card" style={{
          marginBottom: 16, borderRadius: 10, background: '#f9f0ff',
          border: `2px solid ${isRecording ? '#52c41a' : '#722ed1'}`,
          transition: 'border-color 0.4s',
        }}>
          {/* Controls row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14, flexWrap: 'wrap' }}>
            <Tooltip title={isRecording ? t('voiceNoteStop') : t('voiceNoteRecord')}>
              <Button type="primary" danger={isRecording} shape="circle" size="large"
                icon={isRecording ? <StopOutlined /> : <AudioOutlined />}
                onClick={isRecording ? stopRecording : startRecording}
                disabled={!micSupported || isTranscribing || (!isRecording && processingDone && !!(gptResult || finalTranscript.trim()))}
                style={{
                  width: 56, height: 56, fontSize: 22,
                  backgroundColor: isRecording ? undefined : '#722ed1',
                  borderColor: isRecording ? undefined : '#722ed1',
                  animation: isRecording ? 'pulse 1.2s infinite' : undefined,
                }}
              />
            </Tooltip>

            <div style={{ flex: 1, minWidth: 180 }}>
              {isRecording ? (
                <Space wrap>
                  <span style={{ color: '#ff4d4f', fontWeight: 700, fontSize: 13 }}>● REC</span>
                  <span style={{ fontWeight: 700, color: '#52c41a', fontFamily: 'monospace', fontSize: 15 }}>
                    {formatTime(recordingSeconds)}
                  </span>
                  <Tag color="green" style={{ fontSize: 11 }}>Deepgram LIVE</Tag>
                  {webSpeechSupported && <Tag color="blue" style={{ fontSize: 11 }}>{isRTL ? 'مزدوج' : 'Dual'}</Tag>}
                </Space>
              ) : isTranscribing ? (
                <Space>
                  <LoadingOutlined style={{ color: '#722ed1' }} />
                  <Text style={{ color: '#722ed1' }}>{isRTL ? 'جارٍ إنهاء التفريغ...' : 'Finalising Deepgram...'}</Text>
                </Space>
              ) : gptStatus === 'done' ? (
                <Text style={{ color: '#52c41a', fontWeight: 600, fontSize: 13 }}>
                  🩺 {isRTL ? 'الملف الطبي جاهز — يمكنك الحفظ' : 'Medical history ready — ready to save'}
                </Text>
              ) : gptStatus === 'loading' ? (
                <Space>
                  <LoadingOutlined style={{ color: '#722ed1' }} />
                  <Text style={{ color: '#722ed1', fontSize: 13 }}>
                    {isRTL ? 'GPT يُنشئ الملف الطبي...' : 'GPT building medical history...'}
                  </Text>
                </Space>
              ) : processingDone ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {isRTL ? 'معالجة الصوت...' : 'Processing audio...'}
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {!micSupported
                    ? (isRTL ? 'الميكروفون غير مدعوم' : 'Microphone not supported')
                    : (isRTL ? 'اضغط تسجيل للبدء' : 'Press Record to start')}
                </Text>
              )}
            </div>
          </div>

          {/* Live transcript during recording */}
          {isRecording && liveTranscript && (
            <div style={{ marginBottom: 10, padding: '6px 10px', background: '#f0fff4', borderRadius: 8, border: '1px solid #b7eb8f' }}>
              <Text style={{ fontSize: 13, direction: 'rtl', fontFamily: 'Cairo, Noto Sans Arabic, Arial, sans-serif', color: '#135200' }}>
                {liveTranscript}
              </Text>
            </div>
          )}

          {/* Audio playback */}
          {localAudioUrl && !isRecording && (
            <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0e6ff', borderRadius: 8, border: '1px solid #d3adf7' }}>
              <Text style={{ fontSize: 12, color: '#722ed1', display: 'block', marginBottom: 6 }}>
                <AudioFilled style={{ marginInlineEnd: 5 }} />
                {isRTL ? 'التسجيل الصوتي الكامل' : 'Full audio recording'}
              </Text>
              <audio controls src={localAudioUrl} style={{ width: '100%', height: 36, borderRadius: 6, outline: 'none' }} />
            </div>
          )}

          {/* Step progress + error alerts */}
          {processingDone && (
            <div style={{ marginBottom: 10 }}>
              <StepProgress steps={steps} isRTL={isRTL} />

              {deepgramStatus === 'error' && (
                <Alert type="warning" showIcon style={{ marginTop: 6, fontSize: 12 }}
                  message={`Deepgram: ${deepgramError || (isRTL ? 'فشل التفريغ المباشر' : 'Live transcription failed')}`}
                />
              )}
              {whisperStatus === 'error' && (
                <Alert type="warning" showIcon style={{ marginTop: 6, fontSize: 12 }}
                  message={
                    <Space>
                      <span>Whisper: {whisperError || (isRTL ? 'فشل تفريغ الملف' : 'File transcription failed')}</span>
                      {audioBlobRef.current && (
                        <Button size="small" icon={<ReloadOutlined />}
                          onClick={() => { if (audioBlobRef.current) void runWhisper(audioBlobRef.current); }}>
                          {isRTL ? 'إعادة' : 'Retry'}
                        </Button>
                      )}
                    </Space>
                  }
                />
              )}
              {gptStatus === 'error' && (
                <Alert type="error" showIcon style={{ marginTop: 6, fontSize: 12 }}
                  message={
                    <Space>
                      <span>GPT: {gptError || (isRTL ? 'فشلت المعالجة' : 'Processing failed')}</span>
                      <Button size="small" icon={<ReloadOutlined />}
                        onClick={() => { autoGptRanRef.current = false; void runGpt(deepgramResult, offlineResult); }}>
                        {isRTL ? 'إعادة المحاولة' : 'Retry GPT'}
                      </Button>
                    </Space>
                  }
                />
              )}
            </div>
          )}

          {/* GPT spinner while processing */}
          {gptStatus === 'loading' && (
            <div style={{ textAlign: 'center', padding: '18px 0', color: '#722ed1' }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 30 }} spin />} />
              <div style={{ marginTop: 8, fontSize: 12, color: '#722ed1' }}>
                {isRTL ? 'يُنشئ الملف الطبي الهيكلي...' : 'Generating structured medical history...'}
              </div>
            </div>
          )}

          {/* GPT result — structured medical history */}
          {gptResult && gptStatus === 'done' && !editingFinal && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 8 }}>
                <Text strong style={{ fontSize: 13, color: '#722ed1' }}>
                  🩺 {isRTL ? 'الملف الطبي الهيكلي' : 'Structured Medical History'}
                </Text>
                <Space size={4}>
                  <Tag color="purple" style={{ fontSize: 11 }}>GPT ✓</Tag>
                  <Button size="small" style={{ fontSize: 11 }}
                    onClick={() => { setFinalTranscript(gptResult); setEditingFinal(true); }}>
                    {isRTL ? '✏️ تعديل' : '✏️ Edit'}
                  </Button>
                </Space>
              </div>
              <div style={{
                padding: '14px 18px', borderRadius: 10,
                border: '1.5px solid #d3adf7', background: '#faf5ff',
                fontSize: 13, lineHeight: 2.1, direction: 'rtl',
                fontFamily: 'Cairo, Noto Sans Arabic, Arial, sans-serif',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                maxHeight: 480, overflowY: 'auto',
              }}>
                {gptResult}
              </div>
            </div>
          )}

          {/* Editable textarea (shown after clicking Edit or when GPT failed) */}
          {(editingFinal || gptStatus === 'error') && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <Text style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>
                  {isRTL ? '✏️ تعديل الملف الطبي:' : '✏️ Edit medical history:'}
                </Text>
                {editingFinal && gptResult && (
                  <Button type="link" size="small" style={{ fontSize: 11 }}
                    onClick={() => { setEditingFinal(false); setFinalTranscript(''); }}>
                    {isRTL ? '← رجوع للعرض' : '← Back to view'}
                  </Button>
                )}
              </div>
              <Input.TextArea
                rows={8}
                value={gptStatus === 'error' ? finalTranscript : finalTranscript}
                onChange={(e) => setFinalTranscript(e.target.value)}
                placeholder={gptStatus === 'error'
                  ? (isRTL ? 'أدخل الملاحظة يدوياً...' : 'Enter note manually...')
                  : ''}
                style={{
                  fontSize: 14, lineHeight: 1.9, direction: 'rtl',
                  fontFamily: 'Cairo, Noto Sans Arabic, Arial, sans-serif',
                }}
              />
            </div>
          )}

          {/* Audio upload status */}
          {processingDone && audioBlobRef.current && (
            <div style={{ marginBottom: 10 }}>
              {audioUploadState === 'uploading' && (
                <Alert type="info" showIcon style={{ fontSize: 12 }}
                  message={isRTL ? 'جارٍ رفع الصوت إلى Firebase...' : 'Uploading audio to Firebase...'} />
              )}
              {audioUploadState === 'done' && (
                <Alert type="success" showIcon style={{ fontSize: 12 }}
                  message={isRTL ? '✅ تم رفع الصوت بنجاح' : '✅ Audio uploaded successfully'} />
              )}
              {audioUploadState === 'failed' && (
                <Alert type="error" showIcon style={{ fontSize: 12 }}
                  message={
                    <Space>
                      <span>{isRTL ? 'فشل رفع الصوت' : 'Audio upload failed'}</span>
                      <Button size="small" icon={<ReloadOutlined />} onClick={retryAudioUpload}>
                        {isRTL ? 'إعادة رفع الصوت' : 'Retry audio upload'}
                      </Button>
                    </Space>
                  }
                />
              )}
            </div>
          )}

          {/* Action buttons */}
          <Space wrap className="voice-note-actions">
            <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveNote}
              loading={saving} disabled={!canSave}
              className="voice-note-save-button"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>
              {t('voiceNoteSave')}
            </Button>

            {processingDone && !isRecording && (
              <Popconfirm
                title={isRTL ? 'هل تريد تسجيلاً جديداً؟ البيانات الحالية غير محفوظة.' : 'Start a new recording? Current data is not saved.'}
                onConfirm={handleNewRecording}
                okText={isRTL ? 'نعم' : 'Yes'} cancelText={isRTL ? 'لا' : 'No'}>
                <Button icon={<AudioOutlined />} disabled={isTranscribing}>
                  {isRTL ? '🎙 تسجيل جديد' : '🎙 New Recording'}
                </Button>
              </Popconfirm>
            )}

            {(gptResult || (editingFinal && finalTranscript.trim())) ? (
              <Popconfirm
                title={isRTL
                  ? 'سيتم فقدان الملف الطبي غير المحفوظ. هل تريد الإلغاء؟'
                  : 'Unsaved medical history will be lost. Cancel anyway?'}
                onConfirm={handleCancelRecorder}
                okText={isRTL ? 'نعم، إلغاء' : 'Yes, discard'}
                cancelText={isRTL ? 'لا، ابق' : 'No, keep'}
                okType="danger"
                placement="top"
              >
                <Button disabled={saving}>
                  {t('voiceNoteCancelRecord')}
                </Button>
              </Popconfirm>
            ) : (
              <Button onClick={handleCancelRecorder} disabled={saving}>
                {t('voiceNoteCancelRecord')}
              </Button>
            )}
          </Space>
        </Card>
      )}

      {/* ── Saved notes list ───────────────────────────────────────────── */}
      {notes.length === 0 && !showRecorder ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Text type="secondary">{t('voiceNoteEmpty')}</Text>}
          style={{ margin: '12px 0' }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {notes.map((note, idx) => {
            const isExpanded = expandedNotes.has(idx);
            const cleanText  = hasLegacyMarkers(note.text) ? stripLegacyMarkers(note.text) : note.text;
            const preview    = cleanText.slice(0, 150);
            const needsExpand = cleanText.length > 150;
            const hasAudio   = !!(note.audioUrl || note.audioData);

            return (
              <Card key={note._id || idx} size="small"
                style={{ borderRadius: 8, border: '1px solid #d9d0ef', background: '#faf5ff' }}
                bodyStyle={{ padding: '10px 14px' }}>
                {editingIndex === idx ? (
                  <div>
                    <Input.TextArea rows={8} value={editText}
                      onChange={(e) => setEditText(e.target.value)} autoFocus
                      style={{ direction: 'rtl', fontFamily: 'Cairo, Noto Sans Arabic, Arial, sans-serif', fontSize: 13, marginBottom: 8 }} />
                    <Space>
                      <Button type="primary" size="small" icon={<SaveOutlined />} loading={editSaving}
                        onClick={() => handleSaveEdit(idx)}
                        style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}>{t('save')}</Button>
                      <Button size="small" onClick={() => setEditingIndex(null)}>{t('cancel')}</Button>
                    </Space>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        {/* Meta row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {moment(note.createdAt).format('YYYY-MM-DD HH:mm')}
                            {note.updatedAt && note.updatedAt !== note.createdAt &&
                              <span style={{ marginInlineStart: 6 }}>({isRTL ? 'معدّل' : 'edited'})</span>}
                          </Text>
                          <Tag color="purple" style={{ fontSize: 10, margin: 0 }}>
                            🩺 {isRTL ? 'ملف طبي' : 'Medical'}
                          </Tag>
                          {hasAudio && (
                            <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                              <AudioFilled style={{ marginInlineEnd: 2 }} />{isRTL ? 'صوت' : 'Audio'}
                            </Tag>
                          )}
                        </div>

                        {/* Text content */}
                        <div style={{
                          fontSize: 13, lineHeight: 2, direction: 'rtl',
                          fontFamily: 'Cairo, Noto Sans Arabic, Arial, sans-serif',
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          color: '#1a1a1a',
                        }}>
                          {isExpanded ? cleanText : preview}
                          {needsExpand && !isExpanded && '...'}
                        </div>

                        {needsExpand && (
                          <Button type="link" size="small"
                            style={{ padding: 0, fontSize: 12, color: '#722ed1', height: 'auto' }}
                            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                            onClick={() => setExpandedNotes(prev => {
                              const next = new Set(prev);
                              if (next.has(idx)) next.delete(idx); else next.add(idx);
                              return next;
                            })}>
                            {isExpanded
                              ? (isRTL ? 'عرض أقل' : 'Show less')
                              : (isRTL ? 'عرض الملف كاملاً' : 'Show full report')}
                          </Button>
                        )}

                        {/* Audio player */}
                        {hasAudio && (
                          <div style={{ marginTop: 8 }}>
                            <OfflineAudio controls src={note.audioUrl || note.audioData}
                              style={{ width: '100%', height: 32, borderRadius: 6, outline: 'none' }} />
                          </div>
                        )}
                      </div>

                      <Space size={4} style={{ flexShrink: 0 }}>
                        <Tooltip title={t('voiceNoteEdit')}>
                          <Button type="text" size="small" icon={<EditOutlined />}
                            onClick={() => handleStartEdit(note, idx)} />
                        </Tooltip>
                        <Popconfirm title={t('voiceNoteDeleteConfirm')} onConfirm={() => handleDeleteNote(idx)}
                          okText={isRTL ? 'نعم' : 'Yes'} cancelText={isRTL ? 'لا' : 'No'}
                          placement={isRTL ? 'topLeft' : 'topRight'}>
                          <Tooltip title={t('voiceNoteDelete')}>
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Tooltip>
                        </Popconfirm>
                      </Space>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%   { box-shadow: 0 0 0 0 rgba(255,77,79,.5); }
          70%  { box-shadow: 0 0 0 10px rgba(255,77,79,0); }
          100% { box-shadow: 0 0 0 0 rgba(255,77,79,0); }
        }
      `}</style>
    </div>
  );
});

VoiceNoteSection.displayName = 'VoiceNoteSection';

export default VoiceNoteSection;
